<script setup>
import { computed, nextTick, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import AiStudioAPI from 'dashboard/api/aiStudio';

const { t } = useI18n();
const currentUser = useMapGetter('getCurrentUser');
const conversations = ref([]);
const messages = ref([]);
const models = ref([]);
const activeId = ref(null);
const selectedModel = ref('');
const draft = ref('');
const modelSearch = ref('');
const isSending = ref(false);
const isModelPickerOpen = ref(false);
const messageList = ref(null);
const copiedMessageId = ref(null);

const firstName = computed(
  () =>
    (currentUser.value?.available_name || currentUser.value?.name || '').split(
      ' '
    )[0]
);
const selectedModelData = computed(() =>
  models.value.find(model => model.id === selectedModel.value)
);
const filteredModels = computed(() => {
  const query = modelSearch.value.trim().toLocaleLowerCase();
  if (!query) return models.value;
  return models.value.filter(model =>
    `${model.display_name} ${model.id}`.toLocaleLowerCase().includes(query)
  );
});
const planModels = computed(() =>
  filteredModels.value.filter(model => model.tier === 'plan')
);
const premiumModels = computed(() =>
  filteredModels.value.filter(model => model.tier !== 'plan')
);
const modelGroups = computed(() => [
  {
    key: 'plan',
    label: t('AI_STUDIO.MODEL_PLAN'),
    items: planModels.value,
  },
  {
    key: 'premium',
    label: t('AI_STUDIO.MODEL_PREMIUM'),
    items: premiumModels.value,
  },
]);
const groupedConversations = computed(() => {
  const recent = [];
  const older = [];
  conversations.value.forEach(conversation => {
    const age = Date.now() - new Date(conversation.updated_at).getTime();
    (age < 30 * 24 * 60 * 60 * 1000 ? recent : older).push(conversation);
  });
  return [
    { key: 'recent', label: t('AI_STUDIO.RECENT'), items: recent },
    { key: 'older', label: t('AI_STUDIO.OLDER'), items: older },
  ].filter(group => group.items.length);
});

const providerName = model => model?.meta?.providers?.[0] || '';
const providerSlug = model => {
  if (model?.is_default) return 'navi';
  const source = `${model?.id || ''} ${providerName(model)}`.toLowerCase();
  if (source.includes('minimax')) return 'minimax';
  if (source.includes('openai') || source.includes('gpt')) return 'openai';
  if (source.includes('llama') || source.includes('meta')) return 'meta';
  if (source.includes('deepseek')) return 'deepseek';
  if (source.includes('qwen')) return 'alibabacloud';
  if (source.includes('mistral')) return 'mistralai';
  if (source.includes('gemini') || source.includes('google')) return 'google';
  if (source.includes('claude') || source.includes('anthropic'))
    return 'anthropic';
  if (source.includes('groq')) return 'groq';
  return '';
};
const providerIcon = model => {
  const icons = {
    openai: 'i-logos-openai-icon brightness-0 dark:invert',
    anthropic: 'i-logos-anthropic-icon',
    google: 'i-logos-google-gemini',
    meta: 'i-logos-meta-icon',
    mistralai: 'i-logos-mistral-ai-icon',
  };
  return icons[providerSlug(model)] || '';
};
const providerLogo = model => {
  const logos = {
    navi: '/brand-assets/navi-os-transparent.png',
    deepseek: '/brand-assets/deepseek.svg',
    alibabacloud: '/brand-assets/qwen.svg',
  };
  return logos[providerSlug(model)] || '';
};
const providerLogoClass = () => 'size-5';
const providerLabel = model => {
  const labels = {
    navi: 'Navi',
    minimax: 'MiniMax',
    groq: 'Groq',
    openai: 'OpenAI',
    meta: 'Meta',
    deepseek: 'DeepSeek',
    alibabacloud: 'Alibaba Cloud',
    mistralai: 'Mistral AI',
    google: 'Google',
    anthropic: 'Anthropic',
  };
  return labels[providerSlug(model)] || providerName(model);
};
const providerInitials = model =>
  providerLabel(model)
    .split(/\s+/)
    .map(word => word[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
const providerInitialClass = model =>
  providerSlug(model) === 'navi'
    ? 'bg-n-brand text-white'
    : 'bg-n-alpha-2 text-n-slate-11';
const scrollToBottom = () => {
  nextTick(() =>
    messageList.value?.scrollTo({ top: messageList.value.scrollHeight })
  );
};

const loadConversation = async id => {
  activeId.value = id;
  const { data } = await AiStudioAPI.showConversation(id);
  messages.value = data.messages;
  selectedModel.value = data.conversation.model_id || selectedModel.value;
  scrollToBottom();
};

const load = async () => {
  const [modelResponse, conversationResponse] = await Promise.all([
    AiStudioAPI.models(),
    AiStudioAPI.conversations(),
  ]);
  models.value = modelResponse.data.models || [];
  selectedModel.value =
    models.value.find(model => model.is_default)?.id ||
    models.value[0]?.id ||
    '';
  conversations.value = conversationResponse.data.conversations || [];
};

const createConversation = async () => {
  const { data } = await AiStudioAPI.createConversation(selectedModel.value);
  conversations.value.unshift(data.conversation);
  activeId.value = data.conversation.id;
  messages.value = [];
};

const ensureConversation = async () => {
  if (!activeId.value) await createConversation();
  return activeId.value;
};

const chooseModel = model => {
  selectedModel.value = model.id;
  isModelPickerOpen.value = false;
  modelSearch.value = '';
};

const deleteConversation = async id => {
  if (!window.confirm(t('AI_STUDIO.DELETE_CONFIRM'))) return;
  await AiStudioAPI.deleteConversation(id);
  conversations.value = conversations.value.filter(item => item.id !== id);
  if (activeId.value !== id) return;

  activeId.value = null;
  messages.value = [];
};

const copyMessage = async message => {
  await navigator.clipboard.writeText(message.content);
  copiedMessageId.value = message.id;
  window.setTimeout(() => {
    if (copiedMessageId.value === message.id) copiedMessageId.value = null;
  }, 2000);
};

const refreshUntilDone = (conversationId, attempt = 0) => {
  if (attempt >= 180) return;
  window.setTimeout(async () => {
    const { data } = await AiStudioAPI.showConversation(conversationId);
    if (activeId.value === conversationId) {
      messages.value = data.messages;
      scrollToBottom();
    }
    if (data.messages.some(message => message.status === 'pending')) {
      refreshUntilDone(conversationId, attempt + 1);
    }
  }, 1000);
};

// ===== Anexos e voz (27/08, pedido Ivan: falar, imagem, PDF, audio) =====
const attachInput = ref(null);
const pendingAttachment = ref(null); // {kind, filename, text?, signed_id?}
const isUploadingAttachment = ref(false);
const isRecording = ref(false);
let mediaRecorder = null;
let recordedChunks = [];

const onAttachPick = async event => {
  const file = event.target.files?.[0];
  event.target.value = '';
  if (!file) return;
  isUploadingAttachment.value = true;
  try {
    const { data } = await AiStudioAPI.uploadFile(file);
    if (data.kind === 'unsupported') {
      window.alert(data.error);
      return;
    }
    // Miniatura local (estilo Claude/ChatGPT): imagem aparece de verdade no
    // chip, não só o nome do arquivo.
    if (data.kind === 'image') {
      data.previewUrl = URL.createObjectURL(file);
    }
    pendingAttachment.value = data;
  } catch (error) {
    window.alert(error?.response?.data?.error || t('AI_STUDIO.ATTACH_ERROR'));
  } finally {
    isUploadingAttachment.value = false;
  }
};

// Voz em TEMPO REAL (27/08, pedido Ivan): Web Speech API escreve enquanto
// fala (interim results); navegador sem suporte cai no gravador+transcrição.
let recognition = null;
let draftBase = '';

const startLiveDictation = SpeechRec => {
  recognition = new SpeechRec();
  recognition.lang = 'pt-BR';
  recognition.continuous = true;
  recognition.interimResults = true;
  draftBase = draft.value ? `${draft.value.trim()} ` : '';
  recognition.onresult = event => {
    let texto = '';
    for (let i = 0; i < event.results.length; i += 1) {
      texto += event.results[i][0].transcript;
    }
    draft.value = `${draftBase}${texto}`.trimStart();
  };
  recognition.onend = () => {
    isRecording.value = false;
    recognition = null;
  };
  recognition.onerror = () => {
    isRecording.value = false;
    recognition = null;
  };
  recognition.start();
  isRecording.value = true;
};

const toggleRecording = async () => {
  if (isRecording.value) {
    if (recognition) recognition.stop();
    else mediaRecorder?.stop();
    return;
  }
  const SpeechRec = window.SpeechRecognition || window.webkitSpeechRecognition;
  if (SpeechRec) {
    startLiveDictation(SpeechRec);
    return;
  }
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    recordedChunks = [];
    mediaRecorder = new MediaRecorder(stream);
    mediaRecorder.ondataavailable = e => recordedChunks.push(e.data);
    mediaRecorder.onstop = async () => {
      stream.getTracks().forEach(track => track.stop());
      isRecording.value = false;
      const blob = new Blob(recordedChunks, { type: 'audio/webm' });
      if (!blob.size) return;
      isUploadingAttachment.value = true;
      try {
        const { data } = await AiStudioAPI.uploadFile(
          new File([blob], 'fala.webm', { type: 'audio/webm' })
        );
        if (data.text) draft.value = `${draft.value} ${data.text}`.trim();
      } finally {
        isUploadingAttachment.value = false;
      }
    };
    mediaRecorder.start();
    isRecording.value = true;
  } catch {
    window.alert(t('AI_STUDIO.MIC_ERROR'));
  }
};

const attachmentExtras = () => {
  const anexo = pendingAttachment.value;
  if (!anexo) return {};
  if (anexo.kind === 'image') return { image_signed_id: anexo.signed_id };
  if (anexo.text)
    return { context: `[Anexo: ${anexo.filename}]\n${anexo.text}` };
  return {};
};

// MODO imagem (27/08, pedido Ivan): "Gerar imagem" SELECIONA o modo — fica
// aceso e o enviar passa a gerar imagem por tras dos panos, ate desligar.
const imageMode = ref(false);

const generateImage = async () => {
  if (!draft.value.trim() || isSending.value) return;
  isSending.value = true;
  const prompt = draft.value.trim();
  draft.value = '';
  try {
    const id = await ensureConversation();
    const { data } = await AiStudioAPI.generateImage(id, prompt);
    messages.value.push(data.message);
    scrollToBottom();
    refreshUntilDone(id);
  } finally {
    isSending.value = false;
  }
};

const send = async () => {
  if (imageMode.value) {
    await generateImage();
    return;
  }
  if (!draft.value.trim() || isSending.value || !selectedModel.value) return;
  if (
    selectedModelData.value?.tier !== 'plan' &&
    !window.confirm(t('AI_STUDIO.PAID_MODEL_CONFIRM'))
  )
    return;
  isSending.value = true;
  const content = draft.value.trim();
  draft.value = '';
  try {
    const id = await ensureConversation();
    const { data } = await AiStudioAPI.sendMessage(
      id,
      content,
      selectedModel.value,
      attachmentExtras()
    );
    pendingAttachment.value = null;
    messages.value.push(data.user_message, data.reply);
    scrollToBottom();
    refreshUntilDone(id);
  } finally {
    isSending.value = false;
  }
};

const regenerateMessage = message => {
  const messageIndex = messages.value.findIndex(item => item.id === message.id);
  const previousUserMessage = messages.value
    .slice(0, messageIndex)
    .reverse()
    .find(item => item.role === 'user');
  if (!previousUserMessage) return;

  draft.value = previousUserMessage.content;
  send();
};

onMounted(load);
</script>

<template>
  <div class="flex size-full min-h-0 min-w-0 overflow-hidden bg-n-background">
    <aside
      class="hidden w-60 shrink-0 flex-col border-r border-n-weak bg-n-solid-1 px-3 py-4 lg:flex"
    >
      <button
        class="mb-5 flex w-full items-center gap-2 rounded-xl bg-n-brand px-4 py-3 text-sm font-medium text-white shadow-sm hover:opacity-90"
        @click="createConversation"
      >
        <span class="i-lucide-plus size-4" />
        {{ t('AI_STUDIO.NEW_CONVERSATION') }}
      </button>

      <div class="min-h-0 flex-1 overflow-y-auto">
        <section
          v-for="group in groupedConversations"
          :key="group.key"
          class="mb-5"
        >
          <p
            class="mb-2 px-2 text-[10px] font-semibold uppercase tracking-wider text-n-slate-8"
          >
            {{ group.label }}
          </p>
          <button
            v-for="conversation in group.items"
            :key="conversation.id"
            class="group mb-0.5 flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm text-n-slate-11 hover:bg-n-alpha-2"
            :class="{
              'bg-n-alpha-2 text-n-slate-12': activeId === conversation.id,
            }"
            @click="loadConversation(conversation.id)"
          >
            <span
              class="i-lucide-message-square size-4 shrink-0 text-n-slate-8"
            />
            <span class="min-w-0 flex-1 truncate">{{
              conversation.title
            }}</span>
            <span
              class="i-lucide-trash-2 size-3.5 shrink-0 opacity-0 group-hover:opacity-100"
              @click.stop="deleteConversation(conversation.id)"
            />
          </button>
        </section>
      </div>
    </aside>

    <main class="relative flex min-w-0 flex-1 flex-col bg-n-background">
      <header
        class="flex h-16 shrink-0 items-center border-b border-n-weak px-5 lg:px-8"
      >
        <div>
          <h1 class="text-base font-semibold text-n-slate-12">
            {{ t('AI_STUDIO.TITLE') }}
          </h1>
          <p class="text-xs text-n-slate-9">{{ t('AI_STUDIO.SUBTITLE') }}</p>
        </div>
      </header>

      <section ref="messageList" class="min-h-0 flex-1 overflow-y-auto">
        <div
          v-if="!messages.length"
          class="mx-auto flex min-h-full w-full max-w-3xl flex-col items-center justify-center px-5 pb-16"
        >
          <div class="mb-7 flex items-center gap-3 text-center">
            <span class="i-lucide-sparkles size-7 text-n-brand" />
            <h2 class="text-3xl font-medium tracking-tight text-n-slate-12">
              {{ t('AI_STUDIO.GREETING', { name: firstName }) }}
            </h2>
          </div>
          <div class="w-full">
            <div
              class="rounded-3xl border border-n-weak bg-n-solid-1 p-3 shadow-sm focus-within:border-n-slate-7"
            >
              <div
                v-if="pendingAttachment"
                class="mb-2 flex items-center gap-2 rounded-xl border border-n-weak bg-n-alpha-1 px-3 py-1.5 text-xs text-n-slate-11"
              >
                <img
                  v-if="pendingAttachment.previewUrl"
                  :src="pendingAttachment.previewUrl"
                  :alt="pendingAttachment.filename"
                  class="h-14 w-14 rounded-lg object-cover"
                />
                <span
                  v-else
                  :class="
                    pendingAttachment.kind === 'audio'
                      ? 'i-lucide-music'
                      : 'i-lucide-file-text'
                  "
                  class="size-3.5"
                />
                <span class="max-w-60 truncate">{{
                  pendingAttachment.filename
                }}</span>
                <button
                  class="text-n-slate-9 hover:text-n-ruby-11"
                  @click="pendingAttachment = null"
                >
                  <span class="i-lucide-x size-3.5" />
                </button>
              </div>
              <textarea
                v-model="draft"
                rows="3"
                :placeholder="t('AI_STUDIO.PLACEHOLDER')"
                class="max-h-48 min-h-20 w-full resize-none border-0 bg-transparent px-3 py-2 text-base text-n-slate-12 shadow-none outline-none focus:ring-0 placeholder:text-n-slate-8"
                @keydown.enter.exact.prevent="send"
              />
              <div class="flex items-center gap-2">
                <input
                  ref="attachInput"
                  type="file"
                  accept="image/*,application/pdf,audio/*,text/plain,text/csv,text/markdown,.md,.txt,.csv,.json"
                  class="hidden"
                  @change="onAttachPick"
                />
                <button
                  class="flex items-center gap-2 rounded-full px-3 py-2 text-sm text-n-slate-10 hover:bg-n-alpha-2"
                  :title="t('AI_STUDIO.ATTACH')"
                  :disabled="isUploadingAttachment"
                  @click="attachInput?.click()"
                >
                  <span
                    :class="
                      isUploadingAttachment
                        ? 'i-lucide-loader-circle animate-spin'
                        : 'i-lucide-paperclip'
                    "
                    class="size-4"
                  />
                </button>
                <button
                  class="flex items-center gap-2 rounded-full px-3 py-2 text-sm transition-colors"
                  :class="
                    isRecording
                      ? 'bg-n-ruby-9 text-white'
                      : 'text-n-slate-10 hover:bg-n-alpha-2'
                  "
                  :title="t('AI_STUDIO.SPEAK')"
                  @click="toggleRecording"
                >
                  <span
                    :class="isRecording ? 'i-lucide-square' : 'i-lucide-mic'"
                    class="size-4"
                  />
                </button>
                <button
                  class="flex items-center gap-2 rounded-full px-3 py-2 text-sm transition-colors"
                  :class="
                    imageMode
                      ? 'bg-n-brand text-white'
                      : 'text-n-slate-10 hover:bg-n-alpha-2'
                  "
                  @click="imageMode = !imageMode"
                >
                  <span class="i-lucide-image size-4" />
                  {{ t('AI_STUDIO.GENERATE_IMAGE') }}
                </button>
                <div
                  v-on-clickaway="() => (isModelPickerOpen = false)"
                  class="relative ml-auto"
                >
                  <button
                    class="flex max-w-56 items-center gap-2 rounded-full px-3 py-2 text-sm font-medium text-n-slate-11 hover:bg-n-alpha-2"
                    @click="isModelPickerOpen = !isModelPickerOpen"
                  >
                    <img
                      v-if="providerLogo(selectedModelData)"
                      :src="providerLogo(selectedModelData)"
                      :class="providerLogoClass(selectedModelData)"
                      class="shrink-0 rounded-md object-contain"
                    />
                    <span
                      v-else-if="providerIcon(selectedModelData)"
                      :class="providerIcon(selectedModelData)"
                      class="size-4 shrink-0"
                    />
                    <span
                      v-else
                      :class="providerInitialClass(selectedModelData)"
                      class="grid size-5 shrink-0 place-items-center rounded-md text-[9px] font-bold"
                    >
                      {{ providerInitials(selectedModelData) }}
                    </span>
                    <span class="truncate">{{
                      selectedModelData?.display_name ||
                      t('AI_STUDIO.SELECT_MODEL')
                    }}</span>
                    <span class="i-lucide-chevron-down size-3.5" />
                  </button>

                  <div
                    v-if="isModelPickerOpen"
                    class="absolute bottom-12 right-0 z-30 w-[min(24rem,calc(100vw-2rem))] overflow-hidden rounded-2xl border border-n-weak bg-n-solid-1 shadow-xl"
                  >
                    <div class="border-b border-n-weak p-2.5">
                      <div
                        class="flex items-center gap-2 rounded-xl border border-n-weak bg-n-alpha-1 px-3"
                      >
                        <span class="i-lucide-search size-4 text-n-slate-8" />
                        <input
                          v-model="modelSearch"
                          :placeholder="
                            t('AI_STUDIO.SEARCH_MODELS', {
                              count: models.length,
                            })
                          "
                          class="h-10 min-w-0 flex-1 bg-transparent text-sm text-n-slate-12 outline-none"
                        />
                      </div>
                    </div>
                    <div class="max-h-80 overflow-y-auto p-2">
                      <template v-for="group in modelGroups" :key="group.key">
                        <p
                          v-if="group.items.length"
                          class="px-2 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wide text-n-slate-8"
                        >
                          {{ group.label }}
                        </p>
                        <button
                          v-for="model in group.items"
                          :key="model.id"
                          v-tooltip="t('AI_STUDIO.BRAZIL_DATACENTER')"
                          class="flex w-full items-center gap-3 rounded-xl px-2.5 py-2.5 text-left hover:bg-n-alpha-2"
                          :class="{
                            'bg-n-alpha-2': model.id === selectedModel,
                          }"
                          @click="chooseModel(model)"
                        >
                          <img
                            v-if="providerLogo(model)"
                            :src="providerLogo(model)"
                            :class="providerLogoClass(model)"
                            class="shrink-0 rounded-md object-contain"
                          />
                          <span
                            v-else-if="providerIcon(model)"
                            :class="providerIcon(model)"
                            class="size-5 shrink-0"
                          />
                          <span
                            v-else
                            :class="providerInitialClass(model)"
                            class="grid size-6 shrink-0 place-items-center rounded-md text-[9px] font-bold"
                          >
                            {{ providerInitials(model) }}
                          </span>
                          <span
                            class="min-w-0 flex-1 truncate text-sm font-medium text-n-slate-12"
                          >
                            {{ model.display_name }}
                          </span>
                          <span class="text-[10px] text-n-slate-8">{{
                            providerLabel(model)
                          }}</span>
                          <span
                            v-if="model.id === selectedModel"
                            class="i-lucide-check size-4 text-n-brand"
                          />
                        </button>
                      </template>
                    </div>
                  </div>
                </div>
                <button
                  class="grid size-10 place-items-center rounded-full bg-n-brand text-white disabled:opacity-40"
                  :disabled="!draft.trim() || isSending"
                  @click="send"
                >
                  <span class="i-lucide-arrow-up size-5" />
                </button>
              </div>
            </div>
            <div class="mt-5 flex flex-wrap justify-center gap-2">
              <button
                v-for="action in ['WRITE', 'LEARN', 'CODE', 'CREATE']"
                :key="action"
                class="flex items-center gap-2 rounded-full border border-n-weak bg-n-solid-1 px-4 py-2 text-sm text-n-slate-10 hover:bg-n-alpha-2"
                @click="draft = t(`AI_STUDIO.ACTIONS.${action}_PROMPT`)"
              >
                <span
                  :class="
                    action === 'WRITE'
                      ? 'i-lucide-pen-line'
                      : action === 'LEARN'
                        ? 'i-lucide-graduation-cap'
                        : action === 'CODE'
                          ? 'i-lucide-code-2'
                          : 'i-lucide-image'
                  "
                  class="size-4"
                />
                {{ t(`AI_STUDIO.ACTIONS.${action}`) }}
              </button>
            </div>
            <p class="mt-8 text-center text-xs text-n-slate-8">
              {{ t('AI_STUDIO.MODEL_COUNT', { count: models.length }) }}
            </p>
          </div>
        </div>

        <div
          v-else
          class="mx-auto flex w-full max-w-3xl flex-col gap-7 px-5 py-10 pb-40"
        >
          <article
            v-for="message in messages"
            :key="message.id"
            class="flex gap-3"
            :class="{ 'justify-end': message.role === 'user' }"
          >
            <div
              class="max-w-[85%] text-[15px] leading-7 text-n-slate-12"
              :class="
                message.role === 'user'
                  ? 'rounded-3xl bg-n-alpha-2 px-5 py-3'
                  : 'px-1 py-2'
              "
            >
              <img
                v-if="message.image_url"
                :src="message.image_url"
                :alt="message.content"
                class="mb-1 max-h-[32rem] rounded-2xl object-contain"
              />
              <a
                v-if="message.image_url"
                :href="message.image_download_url || message.image_url"
                download
                class="mb-3 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12"
              >
                <span class="i-lucide-download size-3.5" />
                {{ t('AI_STUDIO.DOWNLOAD_IMAGE') }}
              </a>
              <p class="whitespace-pre-wrap">{{ message.content }}</p>
              <p
                v-if="message.status === 'pending'"
                class="flex items-center gap-2 text-n-slate-8"
              >
                <span class="i-lucide-loader-circle size-4 animate-spin" />
                {{ t('AI_STUDIO.THINKING') }}
              </p>
              <div
                v-if="
                  message.role === 'assistant' &&
                  message.status === 'done' &&
                  message.content
                "
                class="mt-2 flex items-center gap-1"
              >
                <button
                  class="flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs text-n-slate-8 hover:bg-n-alpha-2 hover:text-n-slate-11"
                  @click="copyMessage(message)"
                >
                  <span
                    :class="
                      copiedMessageId === message.id
                        ? 'i-lucide-check'
                        : 'i-lucide-copy'
                    "
                    class="size-3.5"
                  />
                  {{
                    t(
                      copiedMessageId === message.id
                        ? 'AI_STUDIO.COPIED'
                        : 'AI_STUDIO.COPY'
                    )
                  }}
                </button>
                <button
                  class="flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs text-n-slate-8 hover:bg-n-alpha-2 hover:text-n-slate-11"
                  @click="regenerateMessage(message)"
                >
                  <span class="i-lucide-refresh-cw size-3.5" />
                  {{ t('AI_STUDIO.REGENERATE') }}
                </button>
              </div>
            </div>
          </article>
        </div>
      </section>

      <footer
        v-if="messages.length"
        class="absolute inset-x-0 bottom-0 bg-gradient-to-t from-n-background via-n-background to-transparent px-5 pb-5 pt-10"
      >
        <div
          class="mx-auto max-w-3xl rounded-3xl border border-n-weak bg-n-solid-1 p-3 shadow-lg focus-within:border-n-slate-7"
        >
          <div
            v-if="pendingAttachment"
            class="mb-2 flex items-center gap-2 rounded-xl border border-n-weak bg-n-alpha-1 px-3 py-1.5 text-xs text-n-slate-11"
          >
            <img
              v-if="pendingAttachment.previewUrl"
              :src="pendingAttachment.previewUrl"
              :alt="pendingAttachment.filename"
              class="h-14 w-14 rounded-lg object-cover"
            />
            <span
              v-else
              :class="
                pendingAttachment.kind === 'audio'
                  ? 'i-lucide-music'
                  : 'i-lucide-file-text'
              "
              class="size-3.5"
            />
            <span class="max-w-60 truncate">{{
              pendingAttachment.filename
            }}</span>
            <button
              class="text-n-slate-9 hover:text-n-ruby-11"
              @click="pendingAttachment = null"
            >
              <span class="i-lucide-x size-3.5" />
            </button>
          </div>
          <textarea
            v-model="draft"
            rows="2"
            :placeholder="t('AI_STUDIO.PLACEHOLDER')"
            class="max-h-40 min-h-12 w-full resize-none border-0 bg-transparent px-3 py-2 text-sm text-n-slate-12 shadow-none outline-none focus:ring-0"
            @keydown.enter.exact.prevent="send"
          />
          <div class="flex items-center gap-2">
            <input
              ref="attachInput"
              type="file"
              accept="image/*,application/pdf,audio/*,text/plain,text/csv,text/markdown,.md,.txt,.csv,.json"
              class="hidden"
              @change="onAttachPick"
            />
            <button
              class="flex items-center gap-2 rounded-full px-3 py-2 text-sm text-n-slate-10 hover:bg-n-alpha-2"
              :title="t('AI_STUDIO.ATTACH')"
              :disabled="isUploadingAttachment"
              @click="attachInput?.click()"
            >
              <span
                :class="
                  isUploadingAttachment
                    ? 'i-lucide-loader-circle animate-spin'
                    : 'i-lucide-paperclip'
                "
                class="size-4"
              />
            </button>
            <button
              class="flex items-center gap-2 rounded-full px-3 py-2 text-sm transition-colors"
              :class="
                isRecording
                  ? 'bg-n-ruby-9 text-white'
                  : 'text-n-slate-10 hover:bg-n-alpha-2'
              "
              :title="t('AI_STUDIO.SPEAK')"
              @click="toggleRecording"
            >
              <span
                :class="isRecording ? 'i-lucide-square' : 'i-lucide-mic'"
                class="size-4"
              />
            </button>
            <button
              class="flex items-center gap-2 rounded-full px-3 py-2 text-sm transition-colors"
              :class="
                imageMode
                  ? 'bg-n-brand text-white'
                  : 'text-n-slate-10 hover:bg-n-alpha-2'
              "
              @click="imageMode = !imageMode"
            >
              <span class="i-lucide-image size-4" />{{
                t('AI_STUDIO.GENERATE_IMAGE')
              }}
            </button>
            <div
              v-on-clickaway="() => (isModelPickerOpen = false)"
              class="relative ml-auto"
            >
              <button
                class="flex max-w-52 items-center gap-2 rounded-full px-3 py-2 text-xs text-n-slate-9 hover:bg-n-alpha-2"
                @click="isModelPickerOpen = !isModelPickerOpen"
              >
                <img
                  v-if="providerLogo(selectedModelData)"
                  :src="providerLogo(selectedModelData)"
                  :class="providerLogoClass(selectedModelData)"
                  class="shrink-0 rounded-md object-contain"
                />
                <span
                  v-else-if="providerIcon(selectedModelData)"
                  :class="providerIcon(selectedModelData)"
                  class="size-4 shrink-0"
                />
                <span
                  v-else
                  :class="providerInitialClass(selectedModelData)"
                  class="grid size-5 shrink-0 place-items-center rounded-md text-[9px] font-bold"
                >
                  {{ providerInitials(selectedModelData) }}
                </span>
                <span class="truncate">{{
                  selectedModelData?.display_name
                }}</span>
                <span class="i-lucide-chevron-down size-3.5 shrink-0" />
              </button>
              <div
                v-if="isModelPickerOpen"
                class="absolute bottom-12 right-0 z-30 w-[min(24rem,calc(100vw-2rem))] overflow-hidden rounded-2xl border border-n-weak bg-n-solid-1 shadow-xl"
              >
                <div class="border-b border-n-weak p-2.5">
                  <div
                    class="flex items-center gap-2 rounded-xl border border-n-weak bg-n-alpha-1 px-3"
                  >
                    <span class="i-lucide-search size-4 text-n-slate-8" />
                    <input
                      v-model="modelSearch"
                      :placeholder="
                        t('AI_STUDIO.SEARCH_MODELS', { count: models.length })
                      "
                      class="h-10 min-w-0 flex-1 bg-transparent text-sm text-n-slate-12 outline-none"
                    />
                  </div>
                </div>
                <div class="max-h-80 overflow-y-auto p-2">
                  <template v-for="group in modelGroups" :key="group.key">
                    <p
                      v-if="group.items.length"
                      class="px-2 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wide text-n-slate-8"
                    >
                      {{ group.label }}
                    </p>
                    <button
                      v-for="model in group.items"
                      :key="model.id"
                      v-tooltip="t('AI_STUDIO.BRAZIL_DATACENTER')"
                      class="flex w-full items-center gap-3 rounded-xl px-2.5 py-2.5 text-left hover:bg-n-alpha-2"
                      :class="{ 'bg-n-alpha-2': model.id === selectedModel }"
                      @click="chooseModel(model)"
                    >
                      <img
                        v-if="providerLogo(model)"
                        :src="providerLogo(model)"
                        :class="providerLogoClass(model)"
                        class="shrink-0 rounded-md object-contain"
                      />
                      <span
                        v-else-if="providerIcon(model)"
                        :class="providerIcon(model)"
                        class="size-5 shrink-0"
                      />
                      <span
                        v-else
                        :class="providerInitialClass(model)"
                        class="grid size-6 shrink-0 place-items-center rounded-md text-[9px] font-bold"
                      >
                        {{ providerInitials(model) }}
                      </span>
                      <span
                        class="min-w-0 flex-1 truncate text-sm font-medium text-n-slate-12"
                      >
                        {{ model.display_name }}
                      </span>
                      <span
                        v-if="model.id === selectedModel"
                        class="i-lucide-check size-4 text-n-brand"
                      />
                    </button>
                  </template>
                </div>
              </div>
            </div>
            <button
              class="grid size-10 place-items-center rounded-full bg-n-brand text-white disabled:opacity-40"
              :disabled="!draft.trim() || isSending"
              @click="send"
            >
              <span class="i-lucide-arrow-up size-5" />
            </button>
          </div>
        </div>
      </footer>
    </main>
  </div>
</template>
